import { toRaw } from 'vue';

import { CONVERSATION_CHANNEL, LAYOUT_CHANNEL } from '~/constants/conversationModels';
import { Routes } from '~/constants/routes';

/**
 * Service to manage conversation and layout channel subscriptions.
 * Ensures proper callback reference tracking for subscribe/unsubscribe operations.
 */
class ConversationSubscriptionService {
  constructor() {
    this.activeCallbacks = new Map();
    this.realtime = null;
  }

  init(realtime) {
    this.realtime = toRaw(realtime);
  }

  getConversationChannelName(conversationId, routeName) {
    return routeName === Routes.Authoring.name
      ? `${CONVERSATION_CHANNEL.authoring_response}:${conversationId}`
      : `${CONVERSATION_CHANNEL.assist_response}:${conversationId}`;
  }

  getLayoutChannelName(conversationId) {
    return `${LAYOUT_CHANNEL.layout_generation}:${conversationId}`;
  }

  subscribeToChannel(channelName, callback) {
    if (!this.realtime) {
      console.error('[SubscriptionService] realtime not initialized');
      return;
    }

    this.unsubscribeFromChannel(channelName);
    this.activeCallbacks.set(channelName, callback);

    this.realtime.subscribeToUserChannel({
      channel: channelName,
      topic: '',
      cb: callback,
    });
  }

  unsubscribeFromChannel(channelName) {
    if (!this.realtime) {
      return;
    }

    const storedCallback = this.activeCallbacks.get(channelName);
    if (storedCallback) {
      this.realtime.unsubscribeFromUserChannel({
        channel: channelName,
        topic: '',
        cb: storedCallback,
      });
      this.activeCallbacks.delete(channelName);
    }
  }

  subscribe(conversationId, routeName, callback) {
    const channelName = this.getConversationChannelName(conversationId, routeName);
    this.subscribeToChannel(channelName, callback);
  }

  unsubscribe(conversationId, routeName) {
    const channelName = this.getConversationChannelName(conversationId, routeName);
    this.unsubscribeFromChannel(channelName);
  }

  subscribeLayout(conversationId, callback) {
    const channelName = this.getLayoutChannelName(conversationId);
    this.subscribeToChannel(channelName, callback);
  }

  unsubscribeLayout(conversationId) {
    const channelName = this.getLayoutChannelName(conversationId);
    this.unsubscribeFromChannel(channelName);
  }

  unsubscribeAll() {
    for (const channelName of this.activeCallbacks.keys()) {
      this.unsubscribeFromChannel(channelName);
    }
  }

  /**
   * Subscribes to the naming channel.
   * @param {Function} callback - Callback function for messages.
   */
  subscribeNaming(callback) {
    const channelName = CONVERSATION_CHANNEL.naming;
    this.subscribeToChannel(channelName, callback);
  }

  /**
   * Unsubscribes from the naming channel.
   */
  unsubscribeNaming() {
    const channelName = CONVERSATION_CHANNEL.naming;
    this.unsubscribeFromChannel(channelName);
  }

  /**
   * Publishes a message to a user channel.
   * @param {string} channelName - The channel name to publish to.
   * @param {Object} data - The data to publish.
   */
  publish(channelName, data) {
    if (!this.realtime) {
      console.error('[SubscriptionService] realtime not initialized');
      return;
    }

    this.realtime.publishToUserChannel({
      channel: channelName,
      topic: '',
      message: {
        data,
      },
    });
  }
}

export const conversationSubscriptionService = new ConversationSubscriptionService();
